import React from 'react'
import {useState, useEffect} from 'react'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../../../contexts/custom-toast-context'
import {useMiniCartContext} from '../../../contexts/mini-cart-context'
import {useAddToCartConfirmation} from '../../../contexts/add-to-cart-confirmation-context'
import {useAddToCartSource} from '../../../contexts/add-to-cart-source-context'
import {useWishlistActions} from '../../../hooks/use-wishlist-actions'
import {
    resolveAddToCartInteractionLocation,
    sendItemCartEvent,
    setTempRecommendation
} from '../../../utils/gtm-utils'
import {sendBrazeCartEvent} from '../../../utils/braze-utils'
import {trackAddToBagEvent} from '../../../utils/optimizely-web-exp-utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const useQuickViewCart = ({
    product,
    variant,
    quantity,
    isDesktopViewport,
    isWishlistPage,
    wishlistProductId,
    setIsQuickViewOpen,
    primaryCategory,
    hasUnselectedVariations,
    setShowOptionsMessage,
    onMissingVariations,
    intl,
    onAddToCartSuccess,
    onQuickViewClose,
    pathname
}) => {
    const [addToCartError, setAddToCartError] = useState('')

    useEffect(() => {
        setAddToCartError('')
    }, [variant?.productId])

    const {showCustomToast, removeToast} = useCustomToast()
    const navigate = useNavigation()
    const {removeItemFromWishlist} = useWishlistActions()
    const {onOpen: onAddToCartModalOpen, onAutoClose: onAddToCartModalClose} = useMiniCartContext()
    const {openConfirmation} = useAddToCartConfirmation()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {plpName, interactionLocation} = useAddToCartSource()

    // Handle adding items to cart
    const getMatchingGroup = (productInput) => {
        const matchingGroup = productInput?.variationGroups?.find(
            (group) => group.variationValues.color === productInput.variationValues.color
        )

        if (matchingGroup) {
            return matchingGroup.productId
        }

        return null
    }

    const handleAddToCart = async (productSelectionValues) => {
        try {
            const itemAddLocation = resolveAddToCartInteractionLocation(pathname, {
                isWishlistPage,
                plpName,
                interactionLocation
            })
            const productItems = productSelectionValues.map(({product: itemProduct, variant: itemVariant, quantity: itemQuantity}) => ({
                productId: itemVariant.productId,
                productName: itemProduct?.name,
                price: itemVariant.price,
                c_brand: itemProduct?.brand,
                c_currency: itemProduct?.currency,
                c_categories: itemProduct?.c_categories,
                c_refinementColor: itemProduct?.c_refinementColor,
                c_size: itemProduct?.c_size || itemVariant.variationValues?.size,
                c_variationGroup: getMatchingGroup(itemProduct),
                quantity: itemQuantity,
                ...(itemAddLocation && {c_itemAddLocation: itemAddLocation})
            }))

            // GTM Modification
            if (primaryCategory) {
                setTempRecommendation(primaryCategory)
            }

            const basket = await addItemToNewOrExistingBasket(productItems)

            // GTM Modification: Set c_gtm_index from sessionStorage plp-productContext
            try {
                const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
                const quickViewGTMIndex = ctx?.index ?? -1
                if (Number.isFinite(quickViewGTMIndex) && quickViewGTMIndex >= 0) {
                    productItems[0].c_gtm_index = quickViewGTMIndex
                }
            } catch {
                // sessionStorage may not be available or context may be invalid
            }

            let productAdded = basket.productItems.find(item => item.productId === productItems[0].productId)

            sendItemCartEvent(
                {
                    ...productAdded,
                    c_categories: productAdded?.c_categories || productItems[0]?.c_categories
                },
                false,
                null,
                itemAddLocation,
                basket.currency
            )

            // Braze Modification - Send Braze Cart Event
            sendBrazeCartEvent(basket, productSelectionValues[0]?.product, 'add')

            trackAddToBagEvent({
                product,
                sku: productItems[0].productId,
                value: productItems[0].price,
                variationGroupId: productItems[0].c_variationGroup,
                category: primaryCategory?.id,
                source: 'quick-view'
            })

            return productSelectionValues
        } catch (error) {
            console.error('The error occured while adding a product to the basket: ', error)
        }
    }

    // Handle cart item addition with validation
    const handleCartItem = async () => {
        if (hasUnselectedVariations) {
            setShowOptionsMessage(true)
            if (onMissingVariations) {
                onMissingVariations()
            }
            return
        }

        try {
            const itemsAdded = await handleAddToCart([{product, variant, quantity}])

            if (itemsAdded) {
                setAddToCartError('')
                if (onAddToCartSuccess) {
                    onAddToCartSuccess()
                }

                // Remove from wishlist if on wishlist page
                // Use wishlistProductId (the original ID that was saved in wishlist)
                // Not productId/pid/product.id because those change when user selects different variants
                if (isWishlistPage && wishlistProductId) {
                    const productObj = {productId: wishlistProductId, currentProduct: product}
                    const context = /\/cart\/?$/.test(pathname ?? '')
                        ? 'wishlist-cart-carousel-context'
                        : 'add-to-cart-context'
                    await removeItemFromWishlist(productObj, null, context)
                }

                setIsQuickViewOpen(false)
                openConfirmation({product, variant, quantity})
            } else {
                setAddToCartError(
                    intl.formatMessage({
                        id: 'product_view.error.not_added_to_cart',
                        defaultMessage: 'Maximum quantity has been reached'
                    })
                )
            }
        } catch (e) {
            console.log(e)
        }
    }

    return {
        handleCartItem,
        addToCartError
    }
}

export default useQuickViewCart
